"use client"

import { useState, useEffect, useRef } from "react"
import type { GameConfig, Section } from "../AdminApp"
import TeachingAvatar from "../../TeachingAvatar"
import "../styles/LessonPreview.css"

interface LessonPreviewProps {
  gameConfig: GameConfig
  sections: Section[]
  currentSectionIndex: number
  currentStepIndex: number
  onStepClick: (sectionIndex: number, stepIndex: number) => void
}

export default function LessonPreview({
  gameConfig,
  sections,
  currentSectionIndex,
  currentStepIndex,
  onStepClick,
}: LessonPreviewProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const [scale, setScale] = useState(0.5)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const audioControlsRef = useRef<any>(null)

  const currentSection = sections[currentSectionIndex]
  const currentStep = currentSection?.steps[currentStepIndex]

  useEffect(() => {
    const calculateScale = () => {
      if (wrapperRef.current) {
        const wrapperWidth = wrapperRef.current.clientWidth
        const wrapperHeight = wrapperRef.current.clientHeight

        // Calculate scale to fit width (1920px native width)
        const scaleX = wrapperWidth / 1920
        const scaleY = wrapperHeight / 1080

        // Use the smaller scale to ensure it fits
        const newScale = Math.min(scaleX, scaleY, 1) // Never scale up beyond 100%

        setScale(newScale)
        console.log("[v0] Calculated scale:", newScale, "wrapper:", wrapperWidth, "x", wrapperHeight)
      }
    }

    calculateScale()
    window.addEventListener("resize", calculateScale)
    return () => window.removeEventListener("resize", calculateScale)
  }, [])

  const getSectionAvatarConfig = () => {
    const sectionConfig = currentSection?.config || {}

    return {
      avatar: sectionConfig.avatar || gameConfig.avatar || "max",
      avatarAlignment: sectionConfig.avatarAlignment || gameConfig.avatarAlignment || "right",
      avatarPosition: sectionConfig.avatarPosition || gameConfig.avatarPosition,
      avatarScale: sectionConfig.avatarScale || gameConfig.avatarScale || 1,
      mobilePosition: sectionConfig.mobilePosition || gameConfig.mobilePosition,
      mobileScale: sectionConfig.mobileScale || gameConfig.mobileScale || 1,
    }
  }

  const sectionAvatarConfig = getSectionAvatarConfig()
  const isCelebration = currentStep?.type === "celebration"
  const isIntro = currentStep?.type === "intro"

  const avatarProps = {
    lessonId: `preview-${currentSectionIndex}-${currentStepIndex}`,
    mainContent: currentStep?.content?.title,
    stepLocked: false,
    onUnlock: () => {},
    avatar: sectionAvatarConfig.avatar,
    avatarAlignment: sectionAvatarConfig.avatarAlignment,
    avatarPosition: sectionAvatarConfig.avatarPosition,
    avatarScale: sectionAvatarConfig.avatarScale,
    mobilePosition: sectionAvatarConfig.mobilePosition,
    mobileScale: sectionAvatarConfig.mobileScale,
    visualContent: currentStep?.content,
    spokenContent:
      isChecking && selectedAnswer !== null && currentStep?.type === "quiz"
        ? currentStep?.content?.options?.[selectedAnswer]?.feedback
        : currentStep?.content?.spokenContent,
    hideAudioControls: currentStep?.content?.hideAudioControls || false,
    audioSrc: currentStep?.content?.audioSrc,
    srtSrc: currentStep?.content?.srtSrc,
    quizOptions: currentStep?.type === "quiz" ? currentStep?.content?.options : undefined,
    onSelectAnswer: (index: number) => setSelectedAnswer(index),
    selectedAnswer,
    isCorrect:
      isChecking && selectedAnswer !== null && currentStep?.type === "quiz"
        ? currentStep?.content?.options?.[selectedAnswer]?.correct
        : null,
    isChecking,
    isCelebration,
    isSuccess: false,
    isVisualTheme: !!(
      currentStep?.content?.visualizationType ||
      currentStep?.content?.chartData ||
      currentStep?.content?.videoUrl ||
      currentStep?.content?.videoSrc ||
      currentStep?.content?.fullHeightVideo
    ),
    audioControlsRef,
    initialControlsVisible: true,
    state:
      currentStep?.type === "quiz" && isChecking && selectedAnswer !== null
        ? currentStep?.content?.options?.[selectedAnswer]?.correct
          ? "happy"
          : "thinking"
        : "wave",
  }

  const handleCheckAnswer = () => {
    if (currentStep?.type === "quiz" && selectedAnswer !== null && !isChecking) {
      setIsChecking(true)
    } else if (isChecking) {
      setIsChecking(false)
      setSelectedAnswer(null)
    }
  }

  useEffect(() => {
    setSelectedAnswer(null)
    setIsChecking(false)
  }, [currentSectionIndex, currentStepIndex])

  return (
    <div className="lesson-preview-container">
      {currentSection && currentStep ? (
        <div className="preview-game-wrapper" ref={wrapperRef}>
          <div
            className="preview-game-viewport"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <main className="preview-main-content">
              <TeachingAvatar {...avatarProps} />
            </main>
          </div>
        </div>
      ) : (
        <div className="preview-game-wrapper">
          <div className="preview-loading">Loading preview...</div>
        </div>
      )}

      <div className="preview-admin-controls">
        <div className="preview-controls">
          <button
            className="preview-control-btn"
            onClick={() => {
              if (currentStepIndex > 0) {
                onStepClick(currentSectionIndex, currentStepIndex - 1)
              } else if (currentSectionIndex > 0) {
                const prevSection = sections[currentSectionIndex - 1]
                onStepClick(currentSectionIndex - 1, prevSection.steps.length - 1)
              }
            }}
            disabled={currentSectionIndex === 0 && currentStepIndex === 0}
          >
            ← Previous
          </button>

          <div className="preview-control-info">
            <strong>{currentSection?.title}</strong>
            <span>
              Step {currentStepIndex + 1}/{currentSection?.steps.length}
            </span>
          </div>

          {currentStep?.type === "quiz" && selectedAnswer !== null && (
            <button className="preview-control-btn preview-check-btn" onClick={handleCheckAnswer}>
              {isChecking ? "Continue" : "Check Answer"}
            </button>
          )}

          <button
            className="preview-control-btn"
            onClick={() => {
              if (currentStepIndex < currentSection?.steps.length - 1) {
                onStepClick(currentSectionIndex, currentStepIndex + 1)
              } else if (currentSectionIndex < sections.length - 1) {
                onStepClick(currentSectionIndex + 1, 0)
              }
            }}
            disabled={
              currentSectionIndex === sections.length - 1 && currentStepIndex === currentSection?.steps.length - 1
            }
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  )
}
